<script setup>
  import {
    computed,
  } from 'vue';

  import img from '@js/image';

  const props = defineProps({
    variant: {
      type: String,
      required: false,
      default: 'A',
      validator(value) {
        return ['A', 'B', 'C', 'D', 'E'].includes(value);
      },
    },
  });

  const CONFIG = {
    A: {
      text: 'Escolha um veículo e ache seu crédito em segundos!',
      illustration: 'Ilustração de um homem e uma mulher usando este simulador no celular',
    },
    B: {
      quote: 'Não importa o seu estilo, a gente tem o seu carro!',
      illustration: 'Ilustração de dois carros modernos e brilhantes, um cinza e um vermelho, estacionados',
    },
    C: {
      quote: 'Não importa o seu estilo, a gente tem sua moto!',
      illustration: 'Ilustração de quatro capacetes de moto de diferentes estilos flutando no ar',
    },
    D: {
      text: 'Escolha um carro e ache seu crédito em segundos!',
      illustration: 'Ilustração de dois carros modernos e brilhantes, um cinza e um vermelho, com símbolos verdes simbolizando uma aprovação de financiamento',
    },
    E: {
      text: 'Escolha uma moto e ache seu crédito em segundos!',
      illustration: 'Ilustração de duas motos modernas e brilhantes, uma criza e uma branca, com símbolos verdes simbolizando uma aprovação de financiamento',
    },
  };

  const { variants } = window.CREDERE;
  const _variant = computed(() => variants?.top_banner || props.variant);

  const classes = computed(() => ([
    'credere-vehicles-showroom-top-banner',
    `variant--${_variant.value}`,
  ]));
</script>

<template>
  <section :class="classes">
    <div class="credere-vehicles-showroom-top-banner--sides">
      <img
        :src="img(`showroom/banners/top/${_variant}-illustration.webp`)"
        :alt="CONFIG[_variant].illustration"
        class="credere-vehicles-showroom-top-banner--illustration"
      >

      <h3
        v-if="CONFIG[_variant].text"
        class="credere-vehicles-showroom-top-banner--header"
      >
        {{ CONFIG[_variant].text }}
      </h3>

      <img
        v-else-if="CONFIG[_variant].quote"
        :alt="CONFIG[_variant].quote"
        :src="img(`showroom/banners/top/${_variant}-quote.webp`)"
        class="credere-vehicles-showroom-top-banner--quote"
      >

      <p class="credere-vehicles-showroom-top-banner--paragraph">
        <b class="credere-vehicles-showroom-top-banner--call">
          Simule já o seu crédito!
        </b>

        É rápido e sem compromisso.
      </p>
    </div>
  </section>
</template>

<style lang="scss">
  .credere-vehicles-showroom-top-banner {
    --fs: var(--token-font-size-mega);
    --line: calc(var(--fs) * var(--token-line-height-s));

    --background-color: var(--token-color-brand-primary);
    --color: var(--token-color-brand-contrast, var(--token-color-constant-light));
    --margin: 0 0 var(--token-spacing-blaster);
    --padding: calc(var(--line) * 1.3) var(--line);

    overflow: hidden;
    position: relative;

    @container (max-width: 768px) {
      --fs: var(--token-font-size-l);

      --margin: 0 0 var(--token-spacing-giga);
    }

    &.variant--B,
    &.variant--C {
      --background-color: #454545;
      --color: var(--token-color-constant-light);
    }

    @container (max-width: 768px) {
      --padding: var(--line);
    }
  }

  .credere-vehicles-showroom-top-banner--sides {
    --gap: calc(var(--line) / 2);
    --margin: 0 auto;

    align-items: center;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    min-height: calc(var(--line) * 4);
    max-width: calc(var(--line) * 28);

    @mixin mobile($mw: 20, $padding: 2) {
      --mw: calc(var(--line) * #{$mw});

      --padding: 0 calc(var(--mw) / #{$padding}) 0 0;

      align-items: flex-start;
      flex-direction: column;
      justify-content: center;
      max-width: var(--mw);
    }

    .variant--D &,
    .variant--E & {
      @include mobile(22, 1.7);
    }

    @container (max-width: 768px) {
      [class*="variant--"] & {
        @include mobile;
      }
    }
  }

  .credere-vehicles-showroom-top-banner--header,
  .credere-vehicles-showroom-top-banner--paragraph {
    max-width: 30%;
    position: relative;

    @mixin mobile {
      max-width: none;
    }

    .variant--D &,
    .variant--E & {
      @include mobile;
    }

    @container (max-width: 768px) {
      [class*="variant--"] & {
        @include mobile;
      }
    }
  }

  .credere-vehicles-showroom-top-banner--header {
    --font-size: var(--fs);
    --font-weight: var(--token-font-weight-bold);
    --line-height: var(--token-line-height-s);
  }

  .credere-vehicles-showroom-top-banner--paragraph {
    --font-size: var(--token-font-size-xxl);

    .variant--D &,
    .variant--E & {
      --font-size: var(--token-font-size-xl);
    }

    @container (max-width: 768px) {
      [class*="variant--"] & {
        --font-size: var(--token-font-size-s);
      }
    }
  }

  .credere-vehicles-showroom-top-banner--quote {
    height: calc(var(--line) * 4);
    position: relative;
  }

  .credere-vehicles-showroom-top-banner--call {
    --font-weight: var(--token-font-weight-bold);

    display: block;

    @container (max-width: 768px) {
      display: inline;
    }
  }

  .credere-vehicles-showroom-top-banner--illustration {
    bottom: 0;
    display: block;
    position: absolute;
    height: 100%;
    left: 50%;
    transform: translateX(-50%);

    .variant--B &,
    .variant--C & {
      left: 48%;
    }

    @mixin mobile {
      left: 50%;
      transform: none;
    }

    .variant--D &,
    .variant--E & {
      @include mobile;
    }

    @container (max-width: 768px) {
      [class*="variant--"] & {
        @include mobile;
      }
    }
  }
</style>
